import {isLocalEntityRef} from './entityLinks';

const QID_PATTERN = /^Q[1-9]\d*$/i;
const PROXY_PREFIX = '/api/entities/';
const NUMERIC_ENTITY_AVATAR = /^\/api\/entities\/(\d+)\/avatar$/;

/**
 * @param {string|null|undefined} id
 * @returns {string|null}
 */
export function normalizeWikidataQid(id) {
    if (!id || typeof id !== 'string') {
        return null;
    }
    const qid = id.trim().toUpperCase();
    return QID_PATTERN.test(qid) ? qid : null;
}

/**
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
export function isEntityAvatarProxyUrl(url) {
    return typeof url === 'string' && url.startsWith(PROXY_PREFIX);
}

/**
 * Prefer dash-api avatar proxy (S3-backed) over Wikidata/Commons direct URLs.
 *
 * @param {{ wikidataId?: string|null, entityId?: number|string|null, imageUrl?: string|null }} params
 * @returns {string|null}
 */
export function resolveEntityAvatarUrl({wikidataId, entityId, imageUrl} = {}) {
    const qid = normalizeWikidataQid(wikidataId);
    if (qid) {
        return `/api/entities/wikidata/${qid}/avatar`;
    }
    if (entityId != null && entityId !== '') {
        const numericId = Number(entityId);
        if (!Number.isNaN(numericId) && numericId > 0) {
            return `/api/entities/${numericId}/avatar`;
        }
    }
    return imageUrl || null;
}

/**
 * Picture URL shared by Chart entity cards and Entity info when that entity is on a Chart.
 * Stored card images win; otherwise the same avatar proxy Entity info uses for QIDs.
 *
 * @param {{ wikidataId?: string|null, entityId?: number|string|null, imageUrl?: string|null }} params
 * @returns {string}
 */
export function resolveChartEntityImageUrl({wikidataId, entityId, imageUrl} = {}) {
    const stored = typeof imageUrl === 'string' ? imageUrl.trim() : '';
    if (stored && !stored.startsWith('blob:') && !stored.startsWith('data:')) {
        const path = stored.split('?')[0];
        const numeric = path.match(NUMERIC_ENTITY_AVATAR);
        if (numeric) {
            const pathId = Number(numeric[1]);
            const currentId = entityId != null && entityId !== '' ? Number(entityId) : NaN;
            if (Number.isNaN(currentId) || pathId !== currentId) {
                const fromProxy = resolveEntityAvatarUrl({
                    wikidataId,
                    entityId,
                    imageUrl: stored,
                });
                if (fromProxy) {
                    return fromProxy;
                }
            }
        }
        return stored;
    }
    const fromProxy = resolveEntityAvatarUrl({
        wikidataId,
        entityId,
        imageUrl: stored || null,
    });
    if (fromProxy) {
        return fromProxy;
    }
    const ref = wikidataId || entityId;
    if (ref != null && ref !== '' && isLocalEntityRef(String(ref))) {
        return `/api/entities/${encodeURIComponent(String(ref))}/avatar`;
    }
    return '';
}

/**
 * Strip cache-bust query params so avatar proxy URLs can be compared.
 *
 * @param {string|null|undefined} url
 * @returns {string}
 */
export function entityAvatarBaseUrl(url) {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';
    const [path] = trimmed.split('?');
    return path;
}

/**
 * Append a cache-bust query param for dash-api avatar proxy URLs.
 * The avatar endpoint is cached for up to a year, but the path stays the same after upload.
 *
 * @param {string|null|undefined} url
 * @param {number|string} [version=Date.now()]
 * @returns {string}
 */
export function bustEntityAvatarCacheUrl(url, version = Date.now()) {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (!trimmed) return '';

    const base = entityAvatarBaseUrl(trimmed);
    if (!isEntityAvatarProxyUrl(base)) {
        return trimmed;
    }

    try {
        const parsed = new URL(trimmed, 'http://localhost');
        parsed.searchParams.set('v', String(version));
        return `${parsed.pathname}${parsed.search}`;
    } catch {
        const params = new URLSearchParams(trimmed.includes('?') ? trimmed.split('?')[1] : '');
        params.set('v', String(version));
        return `${base}?${params.toString()}`;
    }
}
